/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { css, jsx } from '@compiled/react';
import { type IntlShape, useIntl } from 'react-intl-next';

import AtlaskitAvatarGroup from '@atlaskit/avatar-group';
import { token } from '@atlaskit/tokens';

import { ElementName } from '../../../../../../constants';
import { messages } from '../../../../../../messages';
import { getFormattedMessageAsString } from '../../../utils';
import { type ElementProps } from '../../index';

const styles = css({
	minWidth: 'fit-content',
	display: 'inline-flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	ul: {
		marginRight: token('space.100'),
		marginTop: 0,
	},
});

const getPersonNameWithPrefix = (
	elementName: ElementName,
	personName: string,
	intl: IntlShape,
): string => {
	switch (elementName) {
		case ElementName.AssignedToGroup:
			return getFormattedMessageAsString(intl, messages.assigned_to, personName);
		case ElementName.OwnedByGroup:
			return getFormattedMessageAsString(intl, messages.owned_by, personName);
		case ElementName.AuthorGroup:
			return getFormattedMessageAsString(intl, messages.created_by, personName);
		default:
			return personName;
	}
};

export type BaseAvatarItemProps = {
	/**
	 * The name of the person in the avatar.
	 */
	name: string;
	/**
	 * The image to be used in an `@atlaskit/avatar - this should be a url to the image src
	 */
	src?: string;
};

export type BaseAvatarGroupElementProps = ElementProps & {
	/**
	 * An array of Avatars to show
	 */
	items?: BaseAvatarItemProps[];
	/**
	 * The maximum number of Avatars to show in the AvatarGroup
	 */
	maxCount?: number;
	/**
	 * Shows a default fallback avatar if no persons in the AvatarGroup.
	 */
	showFallbackAvatar?: boolean;
	/**
	 * Shows a name prefix in the Avatar tooltip (Created by, Assigned To, Owned by)
	 */
	showNamePrefix?: boolean;
};

/**
 * A base element that displays a group of avatars.
 * @internal
 * @param {BaseAvatarGroupElementProps} BaseAvatarGroupElementProps - The props necessary for the AvatarGroup.
 * @see AuthorGroup
 * @see CollaboratorGroup
 */
const BaseAvatarGroup = ({
	items = [],
	maxCount = 4,
	name,
	className,
	testId = 'smart-element-avatar-group',
	showNamePrefix = false,
	showFallbackAvatar = true,
}: BaseAvatarGroupElementProps): JSX.Element | null => {
	const intl = useIntl();

	const data = useMemo(() => {
		if (!items.length && showFallbackAvatar) {
			switch (name) {
				case ElementName.AssignedToGroup:
					return [
						{
							name: getFormattedMessageAsString(intl, messages.unassigned),
						},
					];
				default:
					return [];
			}
		}

		//show a name prefix if there is one Avatar in a group only
		if (name && items.length === 1 && showNamePrefix) {
			return items.map((person) => {
				return {
					...person,
					name: getPersonNameWithPrefix(name, person.name, intl) || person.name,
				};
			});
		}

		return items;
	}, [name, items, showNamePrefix, showFallbackAvatar, intl]);

	if (!data.length) {
		return null;
	}

	return (
		<span
			// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
			css={[styles]}
			data-separator
			data-smart-element={name}
			data-smart-element-avatar-group
			data-testid={testId}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className={className}
		>
			<AtlaskitAvatarGroup
				maxCount={maxCount}
				appearance="stack"
				size="small"
				data={data}
				testId={testId}
			/>
		</span>
	);
};

export default BaseAvatarGroup;

export const toAvatarGroupProps = (
	items?: BaseAvatarItemProps[],
	showFallbackAvatar?: boolean,
): Partial<BaseAvatarGroupElementProps> | undefined => {
	return items ? { items } : showFallbackAvatar ? { items: [] } : undefined;
};
